import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { ChevronRight, MoreHorizontal } from "lucide-react"

import { cn } from "@/lib/utils"

interface BreadcrumbLinkProps
    extends React.ComponentPropsWithoutRef<"a"> {
    asChild?: boolean;
}

interface BreadcrumbSeparatorProps
    extends React.ComponentPropsWithoutRef<"li"> {
    children?: React.ReactNode;
}

const Breadcrumb = React.forwardRef<HTMLElement, React.ComponentPropsWithoutRef<"nav">>(
  ({ ...props }, ref) => <nav ref={ref} aria-label="breadcrumb" {...props} />
)
Breadcrumb.displayName = "Breadcrumb"

const BreadcrumbList = React.forwardRef<HTMLOListElement, React.ComponentPropsWithoutRef<"ol">>(({ className, ...props }, ref) => (
  <ol
    ref={ref}
    className={cn(
      "flex flex-wrap items-center gap-1.5 break-words text-sm text-muted-foreground sm:gap-2.5",
      className
    )}
    {...props} />
))
BreadcrumbList.displayName = "BreadcrumbList"

const BreadcrumbItem = React.forwardRef<HTMLLIElement,React.ComponentPropsWithoutRef<"li">>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn("inline-flex items-center gap-1.5", className)}
    {...props} />
))
BreadcrumbItem.displayName = "BreadcrumbItem"

const BreadcrumbLink = React.forwardRef<HTMLAnchorElement, BreadcrumbLinkProps>(({ asChild, className, ...props }, ref) => {
  const Comp = asChild ? Slot : "a"

  return (
    <Comp
      ref={ref}
      className={cn("transition-colors hover:text-foreground", className)}
      {...props} />
  );
})
BreadcrumbLink.displayName = "BreadcrumbLink"

const BreadcrumbPage = React.forwardRef<HTMLSpanElement, React.ComponentPropsWithoutRef<"span">>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    role="link"
    aria-disabled="true"
    aria-current="page"
    className={cn("font-normal text-foreground", className)}
    {...props} />
))
BreadcrumbPage.displayName = "BreadcrumbPage"

function BreadcrumbSeparator({
    children,
    className,
    ...props
}: BreadcrumbSeparatorProps) {
    return (
        <li
            role="presentation"
            aria-hidden="true"
            className={cn(
                "[&>svg]:h-3.5 [&>svg]:w-3.5",
                className
            )}
            {...props}
        >
            {children ?? <ChevronRight />}
        </li>
    );
}

// const BreadcrumbSeparator = ({
//   children,
//   className,
//   ...props
// }) => (
//   <li
//     role="presentation"
//     aria-hidden="true"
//     className={cn("[&>svg]:w-3.5 [&>svg]:h-3.5", className)}
//     {...props}>
//     {children ?? <ChevronRight />}
//   </li>
// )

BreadcrumbSeparator.displayName = "BreadcrumbSeparator"

function BreadcrumbEllipsis(
    props: React.ComponentProps<"span">
) {
    return (
        <span
            role="presentation"
            aria-hidden="true"
            className="flex h-9 w-9 items-center justify-center"
            {...props}
        >
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">
                More
            </span>
        </span>
    );
}

// const BreadcrumbEllipsis = ({
//   className,
//   ...props
// }) => (
//   <span
//     role="presentation"
//     aria-hidden="true"
//     className={cn("flex h-9 w-9 items-center justify-center", className)}
//     {...props}>
//     <MoreHorizontal className="h-4 w-4" />
//     <span className="sr-only">More</span>
//   </span>
// )
BreadcrumbEllipsis.displayName = "BreadcrumbElipssis"

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
}
